import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Hook para gerenciar service worker e atualizações
export const useServiceWorker = () => {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [showUpdate, setShowUpdate] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      // Force update check on every page load
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => reg.update());
      });

      navigator.serviceWorker.register('/sw.js').then((reg) => {
        setRegistration(reg);
        
        // Force immediate update check
        reg.update();
        
        // Check if there's already a waiting worker
        if (reg.waiting) {
          // Auto-apply update silently
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Listen for new service worker
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New update available
              setWaitingWorker(newWorker);
              setShowUpdate(true);
            }
          });
        });
      }).catch((error) => {
        console.error('SW registration failed:', error);
      });

      // Handle controller change (when new SW takes over)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });
    }
  }, []);

  // Check for updates periodically (every 5 minutes)
  useEffect(() => {
    if (!registration) return;

    const interval = setInterval(() => {
      registration.update();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [registration]);

  const updateApp = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  const dismissUpdate = () => {
    setShowUpdate(false);
  };

  return { showUpdate, updateApp, dismissUpdate };
};

// Componente de notificação de atualização
export const UpdateNotification = () => {
  const { showUpdate, updateApp, dismissUpdate } = useServiceWorker();

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className="bg-[#0A0A0A] border border-[#00FF94]/30 rounded-2xl p-4 shadow-lg shadow-[#00FF94]/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#00FF94]/20 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-[#00FF94]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold mb-1">Atualização disponível!</h4>
            <p className="text-gray-400 text-sm mb-3">
              Uma nova versão do app está disponível com melhorias e correções.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={updateApp}
                className="bg-[#00FF94] text-black hover:bg-[#00CC76] text-sm px-4 py-2 rounded-full"
                size="sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar agora
              </Button>
              <Button
                onClick={dismissUpdate}
                variant="ghost"
                className="text-gray-400 hover:text-white text-sm px-3 py-2"
                size="sm"
              >
                Depois
              </Button>
            </div>
          </div>
          <button
            onClick={dismissUpdate}
            className="text-gray-500 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;
